import Observable from '../Observable';
// Models
import Validator from '../Validator';
// Interfaces
import IParameter, { IParameterProperty } from '../../../../../types/IParameter';
import IConstraints from '../../../../../types/IContraints';
import { IValidator } from '../Validator/IValidator';
import { isEmptyArray, isEmptyObject } from '../../../../../lib/typeGuards';
import { InputType } from '../../InputTypes';
import { removeControlCharacters } from '../../../../../_lib/StringUtils';

// @ts-expect-error old Form is not typed
export default abstract class Parameter extends Observable implements IParameter {
  // Public fields
  public type: IParameter['type'];
  public constraints: IConstraints;
  public name: IParameter['name'];
  public required: IParameter['required'];
  public multiple: IParameter['multiple'];
  public disabled: IParameter['disabled'];
  public readProtected: IParameter['readProtected'];
  public fieldSet: IParameter['fieldSet'];
  public validator: IValidator;
  public label: IParameter['label']; // label of param, name is often used to create payload
  public issues: any[] = [];
  public preparedValue: IParameter['preparedValue'];

  protected _originalValue: any = null;
  protected _value: any = null;
  constructor(param: IParameterProperty, validator?: IValidator) {
    super();

    this.name = param.name;
    this.type = param.type;
    this.label = param.label;
    if (param.value !== undefined) {
      this._originalValue = param.value;
      this._value = param.value;
    } else if (param.defaultValue) {
      this._value = param.defaultValue;
    }
    this.required = param.required;
    this.multiple = param.multiple;
    this.disabled = param.disabled;
    this.readProtected = param.readProtected;
    // We've to add required to constraints because it's needed for validation issues
    // Backend dudes have to move it into constraints as well
    this.constraints = { ...param.constraints, required: param.required };
    this.fieldSet = param.fieldSet;
    if (param.preparedValue !== undefined) {
      this.preparedValue = param.preparedValue;
    }
    this.validator = validator || new Validator(this.constraints);
    this.getPayloadValue = this.getPayloadValue.bind(this);
    this.getPayloadSortableList = this.getPayloadSortableList.bind(this);
    this.valueChanged = this.valueChanged.bind(this);
    this.getIssues = this.getIssues.bind(this);
    this.isValid = this.isValid.bind(this);
    this.getInputProperties = this.getInputProperties.bind(this);
  }

  get value(): any {
    return this._value;
  }

  set value(value) {
    this._value = value;
    this.validate();
    this.broadcast();
  }

  get originalValue(): any {
    return this._originalValue;
  }

  /**
   * Reset value to original value.
   */
  public reset(broadcast = false): void {
    this._value = this._originalValue;

    if (broadcast) {
      this.broadcast();
    }
  }

  /**
   * Clear value, and original value if hard parameter is true.
   * With parameter false only value is cleared and original value
   * is kept as send from backend.
   * Hard might be needed to be able to send clean new form to backend.
   */
  public clear(hard: boolean): void {
    this._value = undefined;

    if (hard) {
      this._originalValue = undefined;
    }
  }

  public isValid() {
    this.validate();
    return this.issues.length === 0;
  }

  public getIssues(): any[] {
    return this.issues;
  }

  public updateOriginalValue(): void {
    this._originalValue = this.value;
    this.broadcast();
  }

  /**
   * At the beginning _originalValue is either null or '' or {}, after resetting value from _value is undefined.
   */
  public valueChanged(): boolean {
    const equalValues = [null, undefined, ''];
    if (
      (equalValues.includes(this._originalValue) ||
        isEmptyObject(this._originalValue) ||
        isEmptyArray(this._originalValue)) &&
        (equalValues.includes(this._value) || isEmptyObject(this._originalValue) || isEmptyArray(this._value))
    ) {
      return false;
    }
    return JSON.stringify(this._value) !== JSON.stringify(this._originalValue);
  }

  public getPayloadSortableList(): { name: string; value: string } {
    const newValue = { name: this.name, value: '' };
    Object.keys(this._value).forEach((key, index) => {
      newValue.value = newValue.value.concat(this._value[key].id);
      if (index !== Object.keys(this._value).length - 1) {
        newValue.value = newValue.value.concat(', ');
      }
    });

    return newValue;
  }

  public getPayloadValue(): { name: string; value: string } {
    if (this.type === InputType.SORTABLELIST) {
      return this.getPayloadSortableList();
    }

    if (typeof this._value === 'string') {
      return { name: this.name, value: removeControlCharacters(this._value) as string };
    }

    return { name: this.name, value: this._value };
  }

  public validate(value: any = this._value): void {
    this.issues = this.validator.validate(value);
  }

  public getInputProperties() {
    return {
      name: this.name,
      value: this.value,
      constraints: this.constraints,
      isValid: this.isValid,
      getIssues: this.getIssues,
    };
  }
}
